// src/App.jsx
// -----------------------------------------------------------------------------
// Top-level route definitions for the whole app.
//   - Public routes: /login, /forgot-password
//   - Protected routes: everything else, wrapped in <MainLayout> (sidebar +
//     header + footer) and guarded by <ProtectedRoute> so only logged-in
//     users can reach them. Admin-only pages pass allowedRoles.
// -----------------------------------------------------------------------------
import { Routes, Route, Navigate } from 'react-router-dom';

import ProtectedRoute from './routes/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import { ROLES } from './context/AuthContext';
import MyProfile from './pages/Profile/MyProfile';
import AuditLogList from './pages/AuditLog/AuditLogList';
import Reports from './pages/Reports/Reports';
import TicketStatusTransitionList from './pages/Tickets/TicketStatusTransitionList';

// ---------- Public pages ----------
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ChangePassword from './pages/Auth/ChangePassword';
import ResetPassword from './pages/Auth/ResetPassword';

// ---------- Protected pages ----------
import Dashboard from './pages/Dashboard/Dashboard';
import TicketList from './pages/Tickets/TicketList';
import TicketDetail from './pages/Tickets/TicketDetail';
import TicketStatusHistoryList from './pages/Tickets/TicketStatusHistoryList';
import TicketLinkTypeList from './pages/TicketLinkTypes/TicketLinkTypeList';
import TicketMandatoryFieldList from './pages/Tickets/TicketMandatoryFieldList';
import TicketReopenReasonList from './pages/Tickets/TicketReopenReasonList';
import TicketSeverityList from './pages/Tickets/TicketSeverityList';
import TicketStatusList from './pages/Tickets/TicketStatusList';
import ProjectList from './pages/Projects/ProjectList';
import ProjectDetail from './pages/Projects/ProjectDetail';
import ProjectPhaseList from './pages/Projects/ProjectPhaseList';
import ProjectEnvironmentList from './pages/Projects/ProjectEnvironmentList';
import ProjectTeamList from './pages/Projects/ProjectTeamList';
import ProjectTeamMemberList from './pages/Projects/ProjectTeamMemberList';
import UserList from './pages/Users/UserList';
import RoleList from './pages/Roles/RoleList';
import ClientOrgList from './pages/ClientOrganizations/ClientOrgList';
// PermissionList is not routed yet (granular permissions pending a decision)
// import PermissionList from './pages/Permissions/PermissionList';
import TicketTypeList from './pages/TicketTypes/TicketTypeList';
import NotificationList from './pages/Notifications/NotificationList';
import ClientQuestionList from './pages/ClientQuestions/ClientQuestionList';

export default function App() {
  return (
    <Routes>
      {/* ---------- Public routes ---------- */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* ---------- Protected routes (share the Sidebar/Header/Footer shell) ---------- */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Tickets - visible to every logged-in role */}
        <Route path="/tickets" element={<TicketList />} />
        <Route path="/tickets/:ticketId" element={<TicketDetail />} />
        <Route
          path="/ticket-status-history"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER, ROLES.TEAM_MEMBER]}>
              <TicketStatusHistoryList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ticket-link-types"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <TicketLinkTypeList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ticket-mandatory-fields"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER]}>
              <TicketMandatoryFieldList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ticket-reopen-reasons"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER]}>
              <TicketReopenReasonList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ticket-severities"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER]}>
              <TicketSeverityList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ticket-statuses"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER]}>
              <TicketStatusList />
            </ProtectedRoute>
          }
        />

        {/* Projects - Admin / PM / Team Member */}
        <Route
          path="/projects"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER, ROLES.TEAM_MEMBER]}>
              <ProjectList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER, ROLES.TEAM_MEMBER]}>
              <ProjectDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-phases"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER]}>
              <ProjectPhaseList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-environments"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER]}>
              <ProjectEnvironmentList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-teams"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER]}>
              <ProjectTeamList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-team-members"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER]}>
              <ProjectTeamMemberList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-questions"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER]}>
              <ClientQuestionList />
            </ProtectedRoute>
          }
        />

        {/* Admin-only screens */}
        <Route
          path="/users"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <UserList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/roles"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <RoleList />
            </ProtectedRoute>
          }
        />
        {/* Commented out Permissions configuration page
        <Route
          path="/permissions"
          element={
            <ProtectedRoute>
              <PermissionList />
            </ProtectedRoute>
          }
        />
        */}
        <Route
          path="/ticket-types"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <TicketTypeList />
            </ProtectedRoute>
          }
        />

        {/* Admin / PM screen */}
        <Route
          path="/client-organizations"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER]}>
              <ClientOrgList />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ticket-status-transitions"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER]}>
              <TicketStatusTransitionList />
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PROJECT_MANAGER, ROLES.TEAM_MEMBER]}>
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit-log"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <AuditLogList />
            </ProtectedRoute>
          }
        />

        {/* My Profile - everyone (Requirement #24) */}
        <Route path="/profile" element={<MyProfile />} />

        {/* Notifications - everyone */}
        <Route path="/notifications" element={<NotificationList />} />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Route>

      {/* Catch-all -> dashboard (or login, if not authenticated - ProtectedRoute handles that) */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
